/**
 * DTemplate Go-to-Market Slide
 * The round pays for the channels that reach the beachhead's first moms;
 * each mom's network grows the community from there, unpaid
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { EASE_OUT_EXPO } from '@components/motion/scroll-motion';
import { Accent, Callout, StatementSlide, useArrive, useStill } from '@components/press/pitch-deck/slides/dtemplate/frame';
import { colors } from '@config/colors';
import { typography } from '@config/typography';

/*
 * June, 2026-10-09: the pillars — community, online organic, online paid,
 * local virality — drawn as acquisition channels into the beachhead's
 * moms, and the vision of the word of mouth it makes. Then 2026-10-10:
 * "have a loop from virality playing back to Community - Organic, make the
 * current Community Community - Paid, and show that the raising the round
 * goes into Community - Paid and the Online channels".
 *
 * So the round, at the left, pays into three channels (coral, as money
 * is here); every channel brings in the beachhead's moms; each mom who
 * joins brings her network; and her network flows back round, under it
 * all, into Community · Organic, the one channel the round doesn't pay for.
 *
 * June, 2026-10-14: named for two of the conclusion's three loops, the CAC
 * and household virality, and the way back for what it does to the CAC.
 */

/** The beachhead, as every slide names it. */
const BEACHHEAD = 'dual-income California households with kids, earning $250K+';

type Channel = { name: string; text: string; paid: boolean };

const CHANNELS: readonly Channel[] = [
  { name: 'Community · Paid', text: 'Schools, zoos, nanny groups, and moms’ forums on Reddit and Facebook', paid: true },
  { name: 'Online organic', text: 'AEO and AIO with teasers of specialized solutions; memes about being a Home CEO', paid: true },
  { name: 'Online paid', text: 'A multi-armed bandit across Pinterest, Facebook, Instagram, TikTok and Reddit', paid: true },
  { name: 'Community · Organic', text: 'Moms who joined, recommending it in their schools, groups and forums', paid: false },
];

const NETWORK = ['Her partner', 'Her favorite sitters', 'Her parents', 'Her kids’ friends’ moms'];

// ── Geometry, on the body's 1640px ───────────────────────────────────────────

const W = 1640;
const H = 548;

const ROUND = { left: 0, width: 240 };
const COL = { left: 316, width: 500 };
const CARD_H = 104;
const CARD_GAP = 14;
const cardTop = (i: number) => i * (CARD_H + CARD_GAP);
const cardMid = (i: number) => cardTop(i) + CARD_H / 2;

/** The middle of the paid channels, where the round sits, and of all
 *  four, where the moms sit. */
const PAID_MID = cardMid(1);
const ALL_MID = (cardMid(0) + cardMid(3)) / 2;
const ROUND_H = 214;

const SPINE = COL.left + COL.width + 26;
const MOMS = { left: 900, width: 300, height: 176 };
const NET = { left: 1272, width: W - 1272, height: 240 };

/** The way back: from under the network, along the foot, up into
 *  Community · Organic. */
const LOOP_Y = cardTop(3) + CARD_H + 52;
const LOOP_FROM_X = NET.left + NET.width / 2;
const LOOP_TO_X = COL.left + COL.width / 2;
const LOOP = `M ${LOOP_FROM_X} ${ALL_MID + NET.height / 2} V ${LOOP_Y - 24} Q ${LOOP_FROM_X} ${LOOP_Y} ${LOOP_FROM_X - 24} ${LOOP_Y} H ${LOOP_TO_X + 24} Q ${LOOP_TO_X} ${LOOP_Y} ${LOOP_TO_X} ${LOOP_Y - 24} V ${cardTop(3) + CARD_H + 16}`;

export const DGtmSlide: React.FC = () => (
  <StatementSlide
    section="Go-to-market · the CAC and virality loops"
    question="Who is she, and how do you reach her?"
    headline="The round pays to reach the beachhead’s first moms. Their networks grow the community from there."
  >
    <GrowthLoop />
    <Callout order={14}>
      The vision: when a mom complains to another mom, she hears{' '}
      <Accent>“I’m sorry. Have you made a wish in The Home CEO?”</Accent>
    </Callout>
  </StatementSlide>
);

const GrowthLoop: React.FC = () => {
  const arrive = useArrive();
  const still = useStill();
  const fade = (i: number) => ({
    initial: still ? (false as const) : { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.7, ease: EASE_OUT_EXPO, delay: 0.2 + i * 0.12 },
  });
  return (
    <Stage style={{ width: W, height: H }}>
      <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
        {/* The round, into each channel it pays for. */}
        <motion.g {...fade(2)}>
          {CHANNELS.map((channel, i) =>
            channel.paid ? (
              <g key={channel.name}>
                <path
                  d={`M ${ROUND.left + ROUND.width} ${PAID_MID} C ${ROUND.left + ROUND.width + 44} ${PAID_MID}, ${COL.left - 44} ${cardMid(i)}, ${COL.left - 14} ${cardMid(i)}`}
                  stroke={colors.accent.coral.extraDark}
                  strokeWidth="4"
                  strokeLinecap="round"
                  fill="none"
                />
                <Head x={COL.left - 3} y={cardMid(i)} deg={0} fill={colors.accent.coral.extraDark} />
              </g>
            ) : null,
          )}
        </motion.g>
        {/* Every channel, into the moms. */}
        <motion.g {...fade(7)}>
          {CHANNELS.map((channel, i) => (
            <path key={channel.name} d={`M ${COL.left + COL.width} ${cardMid(i)} H ${SPINE}`} stroke={colors.brand.exxtraDark} strokeWidth="3" fill="none" />
          ))}
          <path d={`M ${SPINE} ${cardMid(0)} V ${cardMid(3)}`} stroke={colors.brand.exxtraDark} strokeWidth="3" fill="none" />
          <path d={`M ${SPINE} ${ALL_MID} H ${MOMS.left - 14}`} stroke={colors.brand.exxtraDark} strokeWidth="4" strokeLinecap="round" fill="none" />
          <Head x={MOMS.left - 3} y={ALL_MID} deg={0} fill={colors.brand.exxtraDark} />
        </motion.g>
        {/* The moms, into their networks. */}
        <motion.g {...fade(9)}>
          <path
            d={`M ${MOMS.left + MOMS.width + 6} ${ALL_MID} H ${NET.left - 14}`}
            stroke={colors.brand.exxtraDark}
            strokeWidth="4"
            strokeLinecap="round"
            fill="none"
          />
          <Head x={NET.left - 3} y={ALL_MID} deg={0} fill={colors.brand.exxtraDark} />
        </motion.g>
        {/* Her network, back round into Community · Organic. */}
        <motion.g {...fade(11)}>
          <LoopPath d={LOOP} stroke={colors.brand.exxtraDark} strokeWidth="5" strokeLinecap="round" fill="none" $still={still} />
          <Head x={LOOP_TO_X} y={cardTop(3) + CARD_H + 4} deg={270} fill={colors.brand.exxtraDark} />
        </motion.g>
      </svg>

      <Round style={{ left: ROUND.left, top: PAID_MID - ROUND_H / 2, width: ROUND.width, height: ROUND_H }} {...arrive(1)}>
        <small>The round</small>
        <b>$1–2M</b>
        <span>
          The team, and <Whole>$100–500K</Whole> of acquisition
        </span>
      </Round>

      {CHANNELS.map((channel, i) => (
        <ChannelCard
          key={channel.name}
          $paid={channel.paid}
          style={{ left: COL.left, top: cardTop(i), width: COL.width, height: CARD_H }}
          {...arrive(3 + i)}
        >
          <strong>{channel.name}</strong>
          <span>{channel.text}</span>
        </ChannelCard>
      ))}

      <Moms style={{ left: MOMS.left, top: ALL_MID - MOMS.height / 2, width: MOMS.width, height: MOMS.height }} {...arrive(8, { opacity: 0, scale: 0.92 })}>
        <strong>Beachhead moms</strong>
        <span>{BEACHHEAD}</span>
      </Moms>

      <Network style={{ left: NET.left, top: ALL_MID - NET.height / 2, width: NET.width, height: NET.height }} {...arrive(10)}>
        <strong>Household virality</strong>
        <span>Each mom who joins makes it worth joining for her whole network:</span>
        <People>
          {NETWORK.map((person) => (
            <li key={person}>{person}</li>
          ))}
        </People>
      </Network>

      <LoopLabel style={{ top: LOOP_Y - 17, left: (LOOP_FROM_X + LOOP_TO_X) / 2 - 320, width: 640 }} {...arrive(12, { opacity: 0 })}>
        <span>Her network brings the next moms organically, lowering the CAC</span>
      </LoopLabel>
    </Stage>
  );
};

/** An arrowhead whose tip is at (x, y), pointing `deg` from the right. */
const Head: React.FC<{ x: number; y: number; deg: number; fill: string }> = ({ x, y, deg, fill }) => (
  <path d="M -16 -10 L 0 0 L -16 10 Z" fill={fill} transform={`translate(${x} ${y}) rotate(${deg})`} />
);

// ── Styles ───────────────────────────────────────────────────────────────────

const flow = keyframes`
  to { stroke-dashoffset: -44; }
`;

const Stage = styled.div`
  position: relative;
  flex-shrink: 0;
  margin: 0 auto;

  > svg {
    position: absolute;
    inset: 0;
    overflow: visible;
  }
`;

/* The way back, its dashes flowing round while the slide moves. */
const LoopPath = styled.path<{ $still: boolean }>`
  stroke-dasharray: 14 8;
  animation: ${(p) => (p.$still ? 'none' : flow)} 1.4s linear infinite;
`;

/* The round: money, so the coral the deck keeps for it here. */
const Round = styled(motion.div)`
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px;
  border-radius: 24px;
  border: 3px solid ${colors.accent.coral.extraDark};
  background: ${colors.accent.coral.exxtraLight};
  box-shadow: 0 12px 26px ${colors.shadowSoft};
  font-family: 'Poppins', ${typography.fontFamily.body};

  small {
    font-family: ${typography.fontFamily.small};
    font-size: 15px;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: ${colors.accent.coral.exxxtraDark};
  }

  b {
    margin-top: 2px;
    font-size: 50px;
    font-weight: 500;
    line-height: 1.1;
    letter-spacing: -0.02em;
    color: ${colors.accent.coral.exxxtraDark};
  }

  > span {
    margin-top: 6px;
    font-size: 17px;
    line-height: 1.35;
    color: ${colors.neutral.darkMedium};
  }
`;

/* A figure kept on one line. */
const Whole = styled.span`
  white-space: nowrap;
`;

const ChannelCard = styled(motion.div)<{ $paid: boolean }>`
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 22px;
  border-radius: 18px;
  border-left: 6px solid ${(p) => (p.$paid ? colors.accent.coral.extraDark : colors.brand.exxtraDark)};
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
  font-family: 'Poppins', ${typography.fontFamily.body};

  strong {
    font-size: 22px;
    font-weight: 600;
    color: ${colors.neutral.extraDark};
  }

  span {
    margin-top: 4px;
    font-size: 17px;
    line-height: 1.35;
    color: ${colors.neutral.darkMedium};
  }
`;

/* The moms the channels bring in, in the deep seafoam the panels use. */
const Moms = styled(motion.div)`
  position: absolute;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 24px;
  border-radius: 28px;
  background: ${colors.brand.exxxxtraDark};
  box-shadow: 0 16px 34px ${colors.shadowHard};
  text-align: center;
  font-family: 'Poppins', ${typography.fontFamily.body};
  color: ${colors.white};

  strong {
    font-size: 26px;
    font-weight: 600;
    white-space: nowrap;
  }

  span {
    margin-top: 8px;
    font-size: 17px;
    line-height: 1.4;
    color: color-mix(in srgb, ${colors.white} 85%, transparent);
  }
`;

const Network = styled(motion.div)`
  position: absolute;
  box-sizing: border-box;
  padding: 22px 24px;
  border-radius: 20px;
  border-top: 6px solid ${colors.accent.coral.extraDark};
  background: ${colors.white};
  box-shadow: 0 10px 22px ${colors.shadowSoft};
  font-family: 'Poppins', ${typography.fontFamily.body};

  strong {
    display: block;
    font-size: 24px;
    font-weight: 600;
    color: ${colors.neutral.extraDark};
  }

  span {
    display: block;
    margin-top: 6px;
    font-size: 17px;
    line-height: 1.4;
    color: ${colors.neutral.darkMedium};
  }
`;

const People = styled.ul`
  margin: 14px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  li {
    padding: 5px 12px;
    border-radius: 9999px;
    border: 1px solid ${colors.accent.coral.light};
    background: ${colors.accent.coral.exxtraLight};
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 15px;
    color: ${colors.accent.coral.exxxtraDark};
  }
`;

/* On the slide's own ground, so the way back passes behind it. */
const LoopLabel = styled(motion.div)`
  position: absolute;
  display: flex;
  justify-content: center;

  span {
    padding: 4px 18px;
    border-radius: 9999px;
    background: ${colors.neutral.ultraLight};
    font-family: 'Poppins', ${typography.fontFamily.body};
    font-size: 18px;
    line-height: 26px;
    color: ${colors.brand.exxxtraDark};
  }
`;
